<script setup lang="ts">
import { ref, watch, nextTick } from "vue";
import { useResizeObserver } from "@vueuse/core";
import { X, GitCompareArrows } from "@lucide/vue";

import type { CompareItem } from "@/composables/useLibrarySelection";

// Persistent "compare-later" tray (RTLCA-4890): shows the session compare set as removable chips so a
// user can gather candidates across searches, then Compare or Clear. Presentational — the host owns the
// set (useLibrarySelection) and what Compare does (navigate to the compare page / open the modal).
const props = defineProps<{
  items: CompareItem[];
  maxItems: number;
}>();

const emit = defineEmits<{
  remove: [id: string];
  clear: [];
  compare: [];
}>();

// The chips row scrolls horizontally but its scrollbar reads as chunky, so it's hidden. Instead, a soft
// gradient fade on each edge signals there's more to scroll — shown only when there's actually content
// off that side (fades out at the ends).
const chipsRef = ref<HTMLElement | null>(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

const updateScrollHints = () => {
  const el = chipsRef.value;
  if (!el) return;
  canScrollLeft.value = el.scrollLeft > 1;
  canScrollRight.value = el.scrollWidth - el.clientWidth - el.scrollLeft > 1;
};

// Recompute when the row resizes (tray width changes with the detail pane) and when chips are added/removed.
useResizeObserver(chipsRef, updateScrollHints);
watch(
  () => props.items.length,
  () => nextTick(updateScrollHints)
);
</script>

<template>
  <div
    role="region"
    aria-label="Comparison set"
    class="bg-slate-0 flex items-center gap-3 border-t border-slate-200 px-4 py-3">
    <!-- Chips: horizontally scrollable, scrollbar hidden, with edge fades hinting scrollability -->
    <div class="relative min-w-0 flex-1">
      <div
        ref="chipsRef"
        class="no-scrollbar flex items-center gap-2 overflow-x-auto"
        @scroll="updateScrollHints">
        <div
          v-for="item in items"
          :key="item.id"
          class="bg-app-blue-50 text-app-blue-900 flex shrink-0 items-center gap-1.5 rounded-full py-1 pr-1.5 pl-3">
          <span class="t-caption max-w-[180px] truncate">{{ item.name }}</span>
          <button
            v-tooltip="{ text: `Remove ${item.name} from comparison`, variant: 'label', placement: 'top' }"
            type="button"
            class="focus-ring interactive-active duration-fast ease-standard hover:bg-app-blue-100 rounded-full p-0.5 transition-colors"
            :aria-label="`Remove ${item.name} from comparison`"
            @click="emit('remove', item.id)">
            <X
              class="icon-xs"
              :stroke-width="2" />
          </button>
        </div>
      </div>

      <!-- Edge fades (decorative): visible only when there's more to scroll on that side -->
      <div
        class="from-slate-0 duration-fast ease-standard pointer-events-none absolute inset-y-0 left-0 w-8 bg-linear-to-r to-transparent transition-opacity"
        :class="canScrollLeft ? 'opacity-100' : 'opacity-0'"
        aria-hidden="true" />
      <div
        class="from-slate-0 duration-fast ease-standard pointer-events-none absolute inset-y-0 right-0 w-8 bg-linear-to-l to-transparent transition-opacity"
        :class="canScrollRight ? 'opacity-100' : 'opacity-0'"
        aria-hidden="true" />
    </div>

    <!-- Actions -->
    <div class="flex shrink-0 items-center gap-2">
      <button
        type="button"
        class="focus-ring interactive-active t-small-bold text-type-muted rounded-8 duration-fast ease-standard px-3 py-2 transition-colors hover:bg-slate-100"
        @click="emit('clear')">
        Clear
      </button>
      <button
        type="button"
        class="focus-ring interactive-active t-small-bold rounded-8 duration-fast ease-standard bg-app-blue-600 dark:bg-app-blue-fill hover:bg-app-blue-700 dark:hover:bg-app-blue-fill-hover flex items-center gap-2 px-4 py-2 text-white transition-all disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="items.length < 2"
        aria-label="Compare selected materials"
        @click="emit('compare')">
        <GitCompareArrows
          class="icon-sm shrink-0"
          :stroke-width="1.5" />
        Compare ({{ items.length }}/{{ maxItems }})
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Hide the horizontal scrollbar — the edge gradients convey scrollability instead. */
.no-scrollbar {
  scrollbar-width: none; /* Firefox */
}
.no-scrollbar::-webkit-scrollbar {
  display: none; /* Chrome / Safari / Edge */
}
</style>
